import { ComponentType, lazy } from 'react';
import { forgetStaleChunkReload, reloadOnStaleChunk } from './staleChunk';

/**
 * A page loaded on demand, from its module's named export: each page (and the libraries only it
 * uses — charts, calendars, maps…) gets its own chunk, out of the one loaded at startup. Its chunk
 * gone with a deploy: the tab reloads on the new version (`reloadOnStaleChunk`).
 */
const lazyPage = <TModule>(load: () => Promise<TModule>, name: keyof TModule) =>
  lazy(() =>
    load().then((module) => {
      forgetStaleChunkReload();
      return { default: module[name] as unknown as ComponentType };
    }, reloadOnStaleChunk)
  );

export const UserContainer = lazyPage(() => import('../pages/UserProfile'), 'UserContainer');
export const Home = lazyPage(() => import('../pages/Home'), 'Home');
export const HomeV2 = lazyPage(() => import('../pages/HomeV2'), 'HomeV2');
export const ForgotPassword = lazyPage(() => import('../pages/Login/ForgotPassword'), 'ForgotPassword');
export const LoginPassword = lazyPage(() => import('../pages/Login/LoginPassword'), 'LoginPassword');
export const LoginEmail = lazyPage(() => import('../pages/Login'), 'LoginEmail');
export const LoginFirst = lazyPage(() => import('../pages/Login/LoginFirst'), 'LoginFirst');
export const ResetPassword = lazyPage(() => import('../pages/Login/ResetPassword'), 'ResetPassword');
export const Saml = lazyPage(() => import('../pages/Login/Saml'), 'Saml');
export const BookingView = lazyPage(() => import('../pages/Booking/View'), 'BookingView');
export const BookingUpdate = lazyPage(() => import('../pages/Booking/Update'), 'BookingUpdate');
export const Spaces = lazyPage(() => import('../pages/Spaces'), 'Spaces');
export const MyBookings = lazyPage(() => import('../pages/MyBookings'), 'MyBookings');
export const Colleagues = lazyPage(() => import('../pages/Colleagues'), 'Colleagues');
export const Version = lazyPage(() => import('../pages/Version'), 'Version');
export const OnboardingStart = lazyPage(() => import('../pages/Onboarding/Start'), 'OnboardingStart');
export const OnboardingWorkHours = lazyPage(() => import('../pages/Onboarding/WorkHours'), 'OnboardingWorkHours');
export const OnboardingDesk = lazyPage(() => import('../pages/Onboarding/Desk'), 'OnboardingDesk');
export const OnboardingColleagues = lazyPage(() => import('../pages/Onboarding/Colleagues'), 'OnboardingColleagues');
export const OnboardingProfilePicture = lazyPage(
  () => import('../pages/Onboarding/ProfilePicture'),
  'OnboardingProfilePicture'
);
export const OnboardingNotifications = lazyPage(
  () => import('../pages/Onboarding/Notifications'),
  'OnboardingNotifications'
);
export const OnboardingFinalize = lazyPage(() => import('../pages/Onboarding/Finalize'), 'OnboardingFinalize');
export const ManageMyResources = lazyPage(() => import('../pages/ManageMyResources'), 'ManageMyResources');
export const MeetUp = lazyPage(() => import('../pages/MeetUp'), 'MeetUp');
export const ResourceTags = lazyPage(() => import('../pages/ResourceTags'), 'ResourceTags');
export const WithUserAnalyticsAccess = lazyPage(
  () => import('../pages/Analytics/WithUserAnalyticsAccess'),
  'WithUserAnalyticsAccess'
);
export const RealTimeGuardCode = lazyPage(() => import('../pages/RealTime/guard-code'), 'RealTimeGuardCode');
export const RealTimeGuardUser = lazyPage(() => import('@jooxter/real-time'), 'RealTimeGuardUser');
export const RtsmGuardV3User = lazyPage(() => import('./RtsmGuardV3User'), 'RtsmGuardV3User');
export const RtsmGuardV3Code = lazyPage(() => import('./RtsmGuardV3Code'), 'RtsmGuardV3Code');
export const RtsmGuardV4Code = lazyPage(() => import('./RtsmGuardV4Code'), 'RtsmGuardV4Code');
export const RtsmGuardV4User = lazyPage(() => import('@jooxter/rtsm'), 'RtsmGuardV4User');
